import { useContext, useEffect, useState } from 'react'
import { Box, Button, Container, Grid, Typography } from "@mui/material"
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useGiftRepository } from '../../../../repositories/GiftSubscription/use-gift-repository'
import { Route } from '../../../../router'
import { AppContext } from '../../../../stores/App/AppStore'
import CommonBackButton from '../../../atoms/CommonBackButton/CommonBackButton'
import MarkdownContent from '../../../atoms/MarkdownContent/MarkdownContent'
import Spacer from '../../../atoms/Spacer/Spacer'
import useTrackPageView from "../../../../helpers/hooks/use-track-page-view"
import useGoogleTagManager from '../../../../helpers/analytics/use-google-tag-manager'
import GtmEventName from '../../../../helpers/analytics/GtmEventName'
import BannerImage from '../../../atoms/BannerImage/BannerImage'
import LocalStorageKeys from '../../../../types/LocalStorageKeys'
import ConfirmationDialog from '../../../molecules/ConfirmationDialog/ConfirmationDialog'
import { useSubscriptionGiftRepository } from '../../../../repositories/SubscriptionGift/use-subscription-gift-repository'
import styles from './SubscriptionGiftRedemptionLandingPage.module.css'
import SubscriptionGiftConfirmationDialog from '../../../organisms/SubscriptionGiftConfirmationDialog/SubscriptionGiftConfirmationDialog'
import GiftBottomSection from '../../../molecules/GiftBottomSection/GiftBottomSection'

const SubscriptionGiftRedemptionLandingPage = () => {
    useTrackPageView(`Gift Subscription Redemption Landing Page`)

    const { t } = useTranslation()
    const { code } = useParams()
    const navigate = useNavigate()
    const { trackButtonClick } = useGoogleTagManager()
    const { state: appState } = useContext(AppContext)
    const { retrieveGiftSubscriptionLandingPage } = useGiftRepository()
    const dialogErrorMessageTitle = t(`sign_up_failure`)
    const dialogErrorMessage = `Subscription Gift has been redeemed or is invalid.`
    const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false)
    const [giftCardURL, setGiftCardURL] = useState(``)
    const [giftMessage, setGiftMessage] = useState(``)
    const [isFAQDialogOpen, setIsFAQDialogOpen] = useState(false)
    const [isTNCDialogOpen, setIsTNCDialogOpen] = useState(false)
    const { retrieveSubscriptionGift } = useSubscriptionGiftRepository()

    useEffect(() => {
        if (!appState.pages.giftSubscriptionLandingPage.recipientLandingBannerImageURL){
            retrieveGiftSubscriptionLandingPage()
        }
        if (code){
            retrieveSubscriptionGift(code ?? ``).then(subscriptionGift => {
                if (!subscriptionGift?.status || subscriptionGift?.status !== `PENDING_REDEMPTION`){
                    setIsErrorDialogOpen(true)

                }else {
                    setGiftCardURL(subscriptionGift?.giftCardImageURL ?? ``)
                    setGiftMessage(subscriptionGift?.recipientMessage ?? ``)
                    localStorage.setItem(LocalStorageKeys.SUBSCRIPTION_GIFT_REDEMPTION_CODE, code)
                }
            }).catch(() => {
                setIsErrorDialogOpen(true)
            })
        }
    }, [])

    const handleRegisterButton = () => {
        trackButtonClick(GtmEventName.SUBSCRIPTION_GIFT_REDEMPTION_LANDING_PAGE_SUBMIT)
        navigate(Route.SUBSCRIPTION_GIFT_REDEMPTION_POINTER + `/${code}`)
    }

    if (!code){
        return (<Container>
            <CommonBackButton path={Route.HOME} />
            <Typography variant="h4">Error Fetching Subscription Gift</Typography>
        </Container>
        )
    }

    const bannerImageURL = giftCardURL ?? appState.pages.giftSubscriptionLandingPage.recipientLandingBannerImageURL

    return (
        <Container>
            <CommonBackButton path={Route.HOME} />
            <Box>
                <BannerImage imageUrl={bannerImageURL} className={styles.bannerImage} alt="logo" />
                <Typography variant={`h6`} width={`100%`} minHeight={`50px`} bgcolor={`#D9D9D9`} textAlign={`center`} justifySelf={`center`} display={`flex`} alignItems={`center`} justifyContent={`center`}>
                    {giftMessage}
                </Typography>
            </Box>
            <Spacer height={20}/>
            <MarkdownContent className={styles.content} content={appState.pages.giftSubscriptionLandingPage.recipientLandingDescription} />
            <GiftBottomSection/>
            <Spacer height={20}/>
            <Grid>
                <Button variant='contained' onClick={handleRegisterButton} fullWidth>{t(`gift_subscription_redemption_landing`)}</Button>
            </Grid>
            <Spacer height={15}/>
            <ConfirmationDialog
                acknowledgeOnly
                buttonText={t(`close`)}
                confirmationTitle={dialogErrorMessageTitle}
                confirmationText={dialogErrorMessage}
                dismissButtonText={t(`common_back_to_home`)}
                defaultOpen={isErrorDialogOpen}
                onConfirm={() => navigate(Route.ROOT)}
            />
            <SubscriptionGiftConfirmationDialog
                faqText={appState.pages.giftSubscriptionLandingPage.faq ?? ``}
                isFAQDialogOpen={isFAQDialogOpen}
                onFAQDialogConfirm={setIsFAQDialogOpen}
                tncText={appState.pages.giftSubscriptionLandingPage.tnc ?? ``}
                isTNCDialogOpen={isTNCDialogOpen}
                onTNCDialogConfirm={setIsTNCDialogOpen}
            />
        </Container>
    )
}

export default SubscriptionGiftRedemptionLandingPage